import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { OtherTenantNotice } from "@/pages/LoginPage";
import { Loader2 } from "lucide-react";

const fieldCls =
  "w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#A3A3A3] focus:outline-none focus:border-[#A78BFA] focus:ring-2 focus:ring-[#A78BFA]/15 transition-colors";

export default function RegisterPage() {
  const { slug } = useParams();
  const { registerTenant } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handle = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await registerTenant(slug, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        password: form.password,
      });
      navigate(`/${slug}/app`, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      testId="register-page"
      subtitle="Benvenuta in AURA"
      title="Crea il tuo account."
      footer={
        <span>
          Hai già un account?{" "}
          <Link to={`/${slug}/login`} data-testid="register-to-login" className="text-[#1A1A1A] underline underline-offset-4">
            Accedi
          </Link>
        </span>
      }
    >
      <OtherTenantNotice slug={slug} />
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Nome e cognome</span>
          <input data-testid="register-name" required minLength={2} value={form.name}
            onChange={handle("name")} placeholder="Il tuo nome" className={fieldCls} />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Email</span>
          <input data-testid="register-email" type="email" required value={form.email}
            onChange={handle("email")} placeholder="tu@email.it" className={fieldCls} />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">
            Telefono <span className="text-[#A3A3A3] normal-case tracking-normal">(opzionale)</span>
          </span>
          <input data-testid="register-phone" type="tel" inputMode="tel" value={form.phone}
            onChange={handle("phone")} placeholder="+39 333 1234567" className={fieldCls} />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Password</span>
          <input data-testid="register-password" type="password" required minLength={8}
            value={form.password} onChange={handle("password")} placeholder="Min. 8 caratteri" className={fieldCls} />
        </label>
        {error && (
          <div data-testid="register-error" className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">
            {error}
          </div>
        )}

        {/* Informativa ex art. 13 GDPR. Va resa PRIMA della raccolta dei dati,
            non dopo: senza di essa la registrazione è un trattamento privo di
            informativa. Titolare è il centro estetico; AURA è responsabile. */}
        <div
          data-testid="register-privacy"
          className="rounded-2xl bg-[#FAFAFA] border border-black/5 px-4 py-3.5 text-[11px] text-[#737373] font-light leading-relaxed"
        >
          Registrandoti crei un account presso il centro estetico che ti ha invitata.
          Il centro tratta i tuoi dati (nome, contatti, prenotazioni e storico dei
          trattamenti) per gestire gli appuntamenti e il programma fedeltà; AURA
          gestisce la piattaforma per suo conto. Puoi chiedere accesso, rettifica o
          cancellazione in qualsiasi momento. Non riceverai comunicazioni
          promozionali senza un tuo consenso separato. Dettagli nell'
          <Link
            to="/privacy"
            target="_blank"
            className="underline underline-offset-2 text-[#4A4A4A] hover:text-[#1A1A1A]"
          >
            informativa sulla privacy
          </Link>
          .
        </div>
        <button type="submit" data-testid="register-submit" disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-60 text-white rounded-full px-6 py-3.5 text-sm tracking-wide">
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? "Creazione…" : "Crea account"}
        </button>
      </form>
    </AuthShell>
  );
}
